import React, { useState, useEffect, useRef, useCallback } from "react";
import Dialog from "@material-ui/core/Dialog";
import { makeStyles } from "@material-ui/core/styles";
import { Sparkles, Loader2, RefreshCw, X } from "lucide-react";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => {
	const dark = theme.palette.type === "dark";
	const textPrimary = dark ? "#f8fafc" : "#111827";
	const textSecondary = dark ? "rgba(248, 250, 252, 0.72)" : "#4b5563";
	const textMuted = dark ? "rgba(248, 250, 252, 0.5)" : "#9ca3af";
	const border = dark
		? "1px solid rgba(255, 255, 255, 0.12)"
		: "1px solid rgba(15, 23, 42, 0.12)";
	const surface = dark ? "#0f172a" : "#ffffff";
	const paneBg = dark ? "rgba(255, 255, 255, 0.03)" : "rgba(248, 250, 252, 0.6)";
	const backdrop = dark ? "rgba(2, 6, 23, 0.72)" : "rgba(15, 23, 42, 0.48)";
	const aiBg = dark
		? "linear-gradient(135deg, rgba(147, 112, 219, 0.22) 0%, rgba(99, 102, 241, 0.18) 100%)"
		: "linear-gradient(135deg, #ede9fe 0%, #ddd6fe 100%)";
	const aiBorder = dark
		? "1px solid rgba(147, 112, 219, 0.3)"
		: "1px solid rgba(124, 58, 237, 0.18)";
	const aiIcon = dark ? "#c4b5fd" : "#7c3aed";
	const t = "all 180ms cubic-bezier(0.4, 0, 0.2, 1)";
	const btnBase = {
		height: 38,
		borderRadius: 10,
		fontSize: "0.95rem",
		fontWeight: 500,
		fontFamily: "inherit",
		textTransform: "none",
		cursor: "pointer",
		transition: t,
		display: "inline-flex",
		alignItems: "center",
		justifyContent: "center",
		gap: 8,
	};

	return {
		paper: {
			borderRadius: 20,
			background: surface,
			boxShadow: dark
				? "0 24px 48px -12px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(255, 255, 255, 0.06)"
				: "0 24px 48px -12px rgba(15, 23, 42, 0.28), 0 0 0 1px rgba(15, 23, 42, 0.04)",
			overflow: "hidden",
			width: "100%",
			maxWidth: 760,
			animation: "$rise 220ms cubic-bezier(0.4, 0, 0.2, 1)",
		},
		backdrop: { backgroundColor: backdrop },
		header: {
			padding: theme.spacing(3, 3, 0, 3),
			display: "flex",
			gap: 14,
			alignItems: "flex-start",
			position: "relative",
		},
		iconBadge: {
			width: 44,
			height: 44,
			borderRadius: 14,
			background: aiBg,
			border: aiBorder,
			display: "inline-flex",
			alignItems: "center",
			justifyContent: "center",
			color: aiIcon,
			flexShrink: 0,
		},
		headText: { flex: 1, minWidth: 0, paddingTop: 2 },
		eyebrow: {
			fontSize: 10,
			fontWeight: 700,
			textTransform: "uppercase",
			letterSpacing: "0.14em",
			color: textMuted,
			marginBottom: 4,
		},
		title: {
			fontSize: 17,
			fontWeight: 700,
			color: textPrimary,
			letterSpacing: "-0.01em",
			lineHeight: 1.35,
			margin: 0,
		},
		subtitle: {
			marginTop: 4,
			fontSize: "0.8rem",
			color: textMuted,
			lineHeight: 1.4,
		},
		closeBtn: {
			position: "absolute",
			top: 12,
			right: 12,
			width: 30,
			height: 30,
			borderRadius: 10,
			border: "none",
			background: "transparent",
			color: textMuted,
			cursor: "pointer",
			display: "inline-flex",
			alignItems: "center",
			justifyContent: "center",
			transition: t,
			"&:hover:not(:disabled)": {
				background: dark ? "rgba(255, 255, 255, 0.06)" : "rgba(15, 23, 42, 0.06)",
				color: textPrimary,
			},
			"&:disabled": { opacity: 0.35, cursor: "not-allowed" },
		},
		body: {
			padding: theme.spacing(2.5, 3, 2.5, 3),
		},
		panes: {
			display: "grid",
			gridTemplateColumns: "1fr 1fr",
			gap: 12,
			[theme.breakpoints.down("xs")]: {
				gridTemplateColumns: "1fr",
			},
		},
		pane: {
			display: "flex",
			flexDirection: "column",
			minWidth: 0,
		},
		paneLabel: {
			fontSize: 11,
			fontWeight: 700,
			textTransform: "uppercase",
			letterSpacing: "0.1em",
			color: textMuted,
			marginBottom: 6,
		},
		paneBox: {
			border,
			borderRadius: 12,
			background: paneBg,
			padding: "10px 12px",
			minHeight: 180,
			maxHeight: 260,
			overflowY: "auto",
			fontSize: "0.875rem",
			lineHeight: 1.55,
			color: textPrimary,
			whiteSpace: "pre-wrap",
			"& p": { margin: 0 },
			"& p + p": { marginTop: 8 },
			"& ul, & ol": { margin: "6px 0", paddingLeft: 18 },
			"& strong": { fontWeight: 700 },
		},
		paneBoxEditable: {
			outline: "none",
			"&:focus": {
				borderColor: dark ? "rgba(91, 124, 255, 0.5)" : "rgba(61, 92, 253, 0.35)",
				boxShadow: "0 0 0 1px rgba(91, 124, 255, 0.15)",
			},
		},
		loadingPane: {
			display: "flex",
			flexDirection: "column",
			alignItems: "center",
			justifyContent: "center",
			gap: 10,
			color: textSecondary,
			fontSize: "0.9rem",
			minHeight: 180,
		},
		actions: {
			display: "flex",
			justifyContent: "flex-end",
			gap: 8,
			padding: theme.spacing(2, 3, 2.5, 3),
			borderTop: dark
				? "1px solid rgba(255, 255, 255, 0.06)"
				: "1px solid rgba(15, 23, 42, 0.06)",
			background: dark ? "rgba(255, 255, 255, 0.02)" : "rgba(248, 250, 252, 0.6)",
		},
		btnGhost: {
			...btnBase,
			padding: "0 16px",
			border,
			background: "transparent",
			color: textSecondary,
			"&:hover:not(:disabled)": {
				background: dark ? "rgba(255, 255, 255, 0.04)" : "rgba(15, 23, 42, 0.04)",
				color: textPrimary,
			},
			"&:disabled": { opacity: 0.45, cursor: "not-allowed" },
		},
		btnConfirm: {
			...btnBase,
			minWidth: 112,
			padding: "0 20px",
			border: "none",
			background: "linear-gradient(135deg, #4f7cff 0%, #3b82f6 100%)",
			color: "#ffffff",
			fontWeight: 500,
			boxShadow: dark
				? "0 6px 14px -4px rgba(37, 99, 235, 0.45)"
				: "0 6px 14px -4px rgba(59, 130, 246, 0.45)",
			"&:hover:not(:disabled)": {
				background: "linear-gradient(135deg, #3f6cf0 0%, #2563eb 100%)",
			},
			"&:disabled": { opacity: 0.55, cursor: "not-allowed", boxShadow: "none" },
		},
		spin: { animation: "$spin 0.8s linear infinite" },
		"@keyframes spin": {
			"0%": { transform: "rotate(0deg)" },
			"100%": { transform: "rotate(360deg)" },
		},
		"@keyframes rise": {
			"0%": { opacity: 0, transform: "translateY(8px) scale(0.98)" },
			"100%": { opacity: 1, transform: "translateY(0) scale(1)" },
		},
	};
});

const AiReformulateDescriptionModal = ({
	open,
	onClose,
	title,
	originalDescription,
	onApply,
}) => {
	const classes = useStyles();
	const editorRef = useRef(null);
	const isMounted = useRef(true);
	const [loading, setLoading] = useState(false);
	const [reformulated, setReformulated] = useState("");

	useEffect(() => {
		isMounted.current = true;
		return () => {
			isMounted.current = false;
		};
	}, []);

	const generate = useCallback(async () => {
		if (!originalDescription || !originalDescription.trim()) return;
		setLoading(true);
		setReformulated("");
		try {
			const { data } = await api.post("/ai-reformulate-crm-description", {
				title: title || "",
				description: originalDescription,
			});
			if (!isMounted.current) return;
			const next = (data?.reformulated || "").trim();
			setReformulated(next);
			setTimeout(() => {
				if (editorRef.current) editorRef.current.innerHTML = next;
			}, 0);
		} catch (err) {
			if (isMounted.current) toastError(err);
		} finally {
			if (isMounted.current) setLoading(false);
		}
	}, [title, originalDescription]);

	useEffect(() => {
		if (open) {
			generate();
		} else {
			setReformulated("");
			setLoading(false);
		}
		// Intencional: só dispara na abertura; "Gerar novamente" chama `generate` direto.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open]);

	const handleApply = () => {
		const edited = editorRef.current?.innerHTML || reformulated;
		if (edited && edited.trim()) onApply(edited);
	};

	// Bloqueia ESC/backdrop enquanto loading via reason filter (substitui
	// disableEscapeKeyDown/disableBackdropClick deprecated no MUI 4.x).
	const handleClose = (_event, reason) => {
		if (loading && (reason === "backdropClick" || reason === "escapeKeyDown")) {
			return;
		}
		if (loading) return;
		onClose();
	};

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			aria-labelledby="ai-reformulate-title"
			PaperProps={{ className: classes.paper, elevation: 0 }}
			BackdropProps={{ className: classes.backdrop }}
		>
			<div className={classes.header}>
				<span className={classes.iconBadge} aria-hidden="true">
					<Sparkles size={22} strokeWidth={1.8} />
				</span>
				<div className={classes.headText}>
					<div className={classes.eyebrow}>Reformular com IA</div>
					<h2 id="ai-reformulate-title" className={classes.title}>
						Descrição reformulada
					</h2>
					<div className={classes.subtitle}>
						Revise o resultado antes de aplicar. Você ainda pode editar o texto.
					</div>
				</div>
				<button
					type="button"
					className={classes.closeBtn}
					onClick={handleClose}
					disabled={loading}
					aria-label="Fechar"
				>
					<X size={16} strokeWidth={2} />
				</button>
			</div>

			<div className={classes.body}>
				<div className={classes.panes}>
					<div className={classes.pane}>
						<div className={classes.paneLabel}>Original</div>
						<div
							className={classes.paneBox}
							dangerouslySetInnerHTML={{ __html: originalDescription || "" }}
						/>
					</div>
					<div className={classes.pane}>
						<div className={classes.paneLabel}>Reformulado</div>
						{loading ? (
							<div className={`${classes.paneBox} ${classes.loadingPane}`}>
								<Loader2 size={22} strokeWidth={2} className={classes.spin} />
								<span>Reformulando com IA…</span>
							</div>
						) : (
							<div
								ref={editorRef}
								className={`${classes.paneBox} ${classes.paneBoxEditable}`}
								contentEditable
								suppressContentEditableWarning
							/>
						)}
					</div>
				</div>
			</div>

			<div className={classes.actions}>
				<button
					type="button"
					className={classes.btnGhost}
					onClick={handleClose}
					disabled={loading}
				>
					Cancelar
				</button>
				<button
					type="button"
					className={classes.btnGhost}
					onClick={generate}
					disabled={loading}
				>
					<RefreshCw size={15} strokeWidth={2} />
					Gerar novamente
				</button>
				<button
					type="button"
					className={classes.btnConfirm}
					onClick={handleApply}
					disabled={loading || !reformulated}
				>
					Aplicar
				</button>
			</div>
		</Dialog>
	);
};

export default AiReformulateDescriptionModal;
